import type { RouteMeta, RouteRecordRaw } from 'vue-router'

import { keys } from '@/app/i18n'
import type { BreadcrumbItem } from '@/app/router/meta'

/**
 * The job-offers module's routes (WP-13, wp/job-offers §3): the six WP-13 rows of the route manifest, paths and names
 * from it (D-15) — the legacy paths (`/re/job-offers/view/:id`, `/re/job-offers/edit/new`, `/re/cvs/preview/:id`, …)
 * reach them through the generated legacy redirects. The two CV form routes (`/re/cvs/new`, `/re/cvs/:id/edit`) are
 * WP-12's (OPEN-2); this module only links to them by name.
 *
 * Legacy carried `module` / `mode` that nothing enforced (NEW-8): the guard checks `resource` (+ `action: 'write'` on
 * the offer form, D-21). `/hr/job-offers/:id` is the same detail under Human resources (§1.2, OPEN-9 (a)), with its own
 * breadcrumb whose first item has no link.
 */
const crumb = (text: string, to?: BreadcrumbItem['to']): BreadcrumbItem => (to ? { text, to } : { text, active: true })

const jobOffersCrumb = crumb(keys.t('jobOffers.list.title.jobOffers'), { name: 'job-offers' })
const cvDatabaseCrumb = crumb(keys.t('jobOffers.cvList.title.page'), { name: 'cvs' })

const JobOfferEditView = () => import('./views/JobOfferEditView.vue')
const JobOfferDetailView = () => import('./views/JobOfferDetailView.vue')

/** Create and edit share one view and one `meta` (R3): legacy title "Edit job offer" on both. */
const offerEditMeta: RouteMeta = {
  software: 're',
  resource: 'job-offers',
  action: 'write',
  navActiveLink: 'job-offers',
  pageTitle: keys.t('jobOffers.edit.title.page'),
  breadcrumb: [jobOffersCrumb, crumb(keys.t('jobOffers.edit.title.page'))],
}

const routes: RouteRecordRaw[] = [
  {
    path: '/re/job-offers',
    name: 'job-offers',
    component: () => import('./views/JobOfferListView.vue'),
    meta: {
      software: 're',
      resource: 'job-offers',
      pageTitle: keys.t('jobOffers.list.title.page'),
      breadcrumb: [crumb(keys.t('jobOffers.list.title.jobOffers'))],
    },
  },
  // Legacy `{ name: 'create-edit-job-offer', params: { id: 'new' } }` (the manifest's value redirect).
  { path: '/re/job-offers/new', component: JobOfferEditView, meta: offerEditMeta },
  {
    path: '/re/job-offers/:id(\\d+)/edit',
    name: 'create-edit-job-offer',
    component: JobOfferEditView,
    meta: offerEditMeta,
  },
  {
    path: '/re/job-offers/:id(\\d+)',
    name: 'view-job-offer',
    component: JobOfferDetailView,
    meta: {
      software: 're',
      resource: 'job-offers',
      navActiveLink: 'job-offers',
      pageTitle: keys.t('jobOffers.detail.title.page'),
      breadcrumb: [jobOffersCrumb, crumb(keys.t('jobOffers.detail.title.details'))],
    },
  },
  {
    // §4.1.5: the same detail under Human resources (Onboarding until 2026-11-03, BR-097; `/on/…` redirects here);
    // the first crumb has no link (`onboarding.js:60-62`).
    path: '/hr/job-offers/:id(\\d+)',
    name: 'hr-view-job-offer',
    component: JobOfferDetailView,
    meta: {
      software: 'hr',
      resource: 'job-offers',
      navActiveLink: 'job-offers',
      pageTitle: keys.t('jobOffers.detail.title.page'),
      breadcrumb: [crumb(keys.t('jobOffers.list.title.jobOffers')), crumb(keys.t('jobOffers.detail.title.page'))],
    },
  },
  {
    path: '/re/cvs',
    name: 'cvs',
    component: () => import('./views/CvListView.vue'),
    meta: {
      software: 're',
      resource: 'cv',
      pageTitle: keys.t('jobOffers.cvList.title.page'),
      breadcrumb: [crumb(keys.t('jobOffers.cvList.title.page'))],
    },
  },
  {
    path: '/re/cvs/:id(\\d+)',
    name: 'cv-preview',
    component: () => import('./views/CvPreviewView.vue'),
    meta: {
      software: 're',
      resource: 'cv',
      navActiveLink: 'cvs',
      pageTitle: keys.t('jobOffers.cvList.title.page'),
      breadcrumb: [cvDatabaseCrumb, crumb(keys.t('jobOffers.cvPreview.title.cvPreview'))],
    },
  },
]

export default routes
